import {
  ChangeDetectorRef,
  Component,
  Optional,
  Self,
  input,
  output,
  ChangeDetectionStrategy,
} from '@angular/core';
import { NgControl } from '@angular/forms';

import type { Observable } from 'rxjs';
import { takeUntil } from 'rxjs';

import { I18nService } from '@core/i18n-legacy';

import { RtInput } from '../../utils/rt-input';
import { InputComponent } from '../input/input.component';

@Component({
  selector: 'rt-numeric-input',
  imports: [InputComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './numeric-input.component.html',
})
export class NumericInputComponent extends RtInput<string> {
  public placeholder = input<string>(this.i18n.translate('app.common.type_number'));
  public updateFormattedValueDispatcher$ = input<Observable<void>>();
  public displayLocaleFormatting = input<boolean>(true);
  public isInvalid = input<boolean | null>(null);
  public disableInvalidStyles = input<boolean>(false);

  public postFormattingValueChange = output<string | null>();

  public displayedValue: string | null;

  constructor(
    private i18n: I18nService,
    cdRef: ChangeDetectorRef,
    @Optional() @Self() ngControl?: NgControl,
  ) {
    super(cdRef, ngControl);
  }

  public ngOnInit(): void {
    this.registerOnTouched(this.onBlur);
    this.setDisplayedValue();

    if (this.updateFormattedValueDispatcher$()) {
      this.updateFormattedValueDispatcher$()!
        .pipe(takeUntil(this.ngOnDestroy$))
        .subscribe(() => {
          if (this.value) {
            this.setDisplayedValue();
            this.cdRef.detectChanges();
          }
        });
    }
  }

  public onBlur = () => {
    const isValid = this.ngControl?.valid;
    if (this.value && isValid) {
      this.setControlValue();
      this.setDisplayedValue();
    } else {
      this.displayedValue = null;
    }

    if (isValid) {
      this.postFormattingValueChange.emit(this.value);
    }

    this.cdRef.detectChanges();
  };

  public onFocus(): void {
    if (this.readOnly()) return;
    this.displayedValue = null;
    this.cdRef.detectChanges();
  }

  public getInvalidValue(): boolean {
    if (this.isInvalid() !== null) {
      return !!this.isInvalid();
    }
    return !!this.ngControl?.invalid;
  }

  private setControlValue(): void {
    // remove all empty spaces and separators except the last one
    const decimalValue = this.ngControl?.value
      .toString()
      .replace(/,/g, '.')
      .replace(/[.](?=.*[.])/g, '')
      .replace(/\s/g, '');

    const formattedValue = Number(decimalValue).toFixed(decimalValue.includes('.') ? 2 : 0);
    this.ngControl?.control?.setValue(formattedValue);
  }

  private setDisplayedValue(): void {
    if (this.displayLocaleFormatting()) {
      this.displayedValue =
        this.value && this.ngControl?.valid ? this.formatNumberToLocaleString(this.value) : null;
    }
  }

  private formatNumberToLocaleString(value: string): string {
    const digitsCount = value.toString().split('.')[1]?.length || 0;
    return Intl.NumberFormat(navigator.language, { minimumFractionDigits: digitsCount }).format(
      Number(value),
    );
  }
}
